import CancelIcon from "@mui/icons-material/Cancel";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  Coupon,
  CreditsInfo,
  HubPlan,
  PlanInfo,
  Subscription,
  UpdateSubscription,
} from "data/models";
import {
  ReactQueryMutationError,
  updateSubscription,
  validateCoupon,
} from "data/mutations";
import {
  fetchCredits,
  fetchPaymentMethod,
  fetchPlans,
  fetchSubscription,
} from "data/queries";
import dayjs from "dayjs";
import { CheckCircleIcon } from "lucide-react";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router";
import * as Routes from "routes";
import { roundDownOverageBudget } from "utils/calculations";
import { formatPrice } from "utils/format";
import { resolveIdentityId } from "utils/identity";
import { ConfirmChangesSection } from "./confirm-changes";
import { ProductCard } from "./product-card";

type ChangeItem = {
  lhs: string;
  rhs?: string;
  hidden?: boolean;
};

export type ChangeSummary = {
  infoText: string;
  changeType: ChangeType;
  subscription: ChangeItem[];
  charges: ChangeItem[];
  totalDue: number | string;
};

type ChangeType = "upgrade" | "downgrade" | "same" | "no_selection";

const formatDate = (dt: dayjs.Dayjs) => {
  return dt.format("D MMM YYYY");
};

const getPlan = (
  productKey: string,
  cadence: "month" | "year",
  planInfo: PlanInfo
) => {
  const validPrices = planInfo.availablePlans.filter(
    (x) => x.deprecated && !x.externally_managed // TODO - deprecated check is inverted. When we go live with new plans this needs to be !x.deprecated
  );

  const productPrices = validPrices.filter((x) => x.product_key === productKey);

  if (!productPrices) {
    return null;
  }
  const matchingPrice = productPrices.filter((x) => x.cadence === cadence);

  if (!matchingPrice) {
    return null;
  }
  return matchingPrice[0];
};

const getSuccessText = (
  selectedPlan: HubPlan | null,
  currentPlan: HubPlan | null
) => {
  if (selectedPlan === null) {
    return "No new plan selected";
  }
  if (currentPlan === null) {
    return `Successfully started a ${selectedPlan.display_name} subscription!`;
  }
  if (currentPlan.tier > selectedPlan.tier) {
    return `Successfully scheduled downgrade from ${currentPlan.display_name} to ${selectedPlan.display_name}`;
  }
  return `Successfully upgradaded from ${currentPlan.display_name} to ${selectedPlan.display_name}`;
};

const getChangeType = (
  selectedPlan: HubPlan | null,
  currentPlan: HubPlan | null
): ChangeType => {
  if (selectedPlan === null) {
    return "no_selection";
  }
  if (currentPlan === null) {
    return "upgrade";
  }
  if (selectedPlan.tier === currentPlan.tier) {
    return "same";
  }
  if (currentPlan.tier > selectedPlan.tier) {
    return "downgrade";
  }

  return "upgrade";
};

export const ManageSubscription = () => {
  const [annualPlans, setAnnualPlans] = useState<boolean>(false);
  const [selectedPrice, setSelectedPrice] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<HubPlan | null>(null);
  const [overageInput, setOverageInput] = useState(0);
  const [newOverageBudget, setNewOverageBudget] = useState(0);

  const plansQuery = useQuery([QueryKeys.plans], () => fetchPlans());
  const [identityState, _] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  const subscriptionQuery = useQuery<Subscription>(
    [QueryKeys.subscription, identityId],
    () => fetchSubscription()
  );
  const paymentMethodQuery = useQuery(
    [QueryKeys.paymentMethod, identityId],
    () => fetchPaymentMethod()
  );
  const creditsQuery = useQuery(
    [QueryKeys.overage, identityId],
    () => fetchCredits(),
    {
      onSuccess: (result: CreditsInfo) => {
        setOverageInput(result.overageDollarsLimit);
      },
    }
  );

  const queryClient = useQueryClient();

  const subscriptionMutation = useMutation(
    (values: UpdateSubscription) => updateSubscription(values),
    {
      onSuccess: (updatedSubscription) => {
        queryClient.setQueryData([QueryKeys.subscription, identityId], {
          ...subscriptionQuery.data,
          activeSubscription: updatedSubscription.activeSubscription,
        });

        // queryClient.setQueryData([QueryKeys.overage, identityId], {
        //   ...overageQuery.data,
        //   dollarsLimit: updatedSubscription.overageLimitDollars,
        // });

        enqueueSnackbar(
          getSuccessText(
            plansQuery.data?.availablePlans.find(
              (x) => x.price_lookup_id === selectedPrice
            ) ?? null,
            subscriptionQuery.data?.activeSubscription?.plan ?? null
          ),
          {
            variant: "success",
          }
        );

        // Redirect
        history.push(Routes.developerDashboard);
      },
    }
  );
  const currentPlan = subscriptionQuery.data?.activeSubscription?.plan ?? null;
  const changeType = getChangeType(selectedPlan, currentPlan);

  const getChangeSummary = (): ChangeSummary => {
    const startDateDt =
      changeType === "upgrade"
        ? dayjs()
        : dayjs(
            (subscriptionQuery.data?.activeSubscription?.renewalDate ?? 1) *
              1000
          );
    const renewalDate = startDateDt.add(1, selectedPlan?.cadence);
    let infoText = "";
    if (changeType === "upgrade") {
      infoText =
        "These changes will apply immediately, and you will be charged the total due below";
    } else if (changeType === "downgrade") {
      infoText =
        "These changes will take effect at the end of your current billing cycle. The due amount will be charged on the renewal date, along with any additional overage incurred";
    } else {
      return {
        infoText: "No plan selected",
        changeType: changeType,
        subscription: [],
        charges: [],
        totalDue: 0,
      };
    }

    let couponText = "";
    if (couponValid) {
      if (couponMutation.data?.duration === "once") {
        couponText = `(First ${selectedPlan?.cadence})`;
      } else {
        couponText = "(Repeating)";
      }
    }

    return {
      infoText: infoText,
      changeType: changeType,
      subscription: [
        { lhs: `Subscribe to ${selectedPlan?.display_name}` },
        { lhs: `Start Date`, rhs: formatDate(startDateDt) },
        { lhs: `Automatically Renews`, rhs: formatDate(renewalDate) },
        {
          lhs: `Starting Credit Balance`,
          rhs: `${selectedPlan?.credit_amount.toLocaleString()} credits`,
        },
        { lhs: "Overage Budget", rhs: `Up to $${newOverageBudget}` },
      ],
      charges: [
        {
          lhs: "Previous Subscription Overage",
          rhs: formatPrice(creditsQuery.data?.overageDollarsSpent ?? 0),
        },
        {
          lhs: `${selectedPlan?.display_name} Subscription`,
          rhs: formatPrice(selectedPlan?.dollar_cost ?? 0),
        },
        {
          lhs: couponMutation.data?.name ?? "",
          rhs: `-$${calculateCouponDiscount(
            selectedPlan?.dollar_cost ?? 0,
            selectedPlan?.price_lookup_id ?? "",
            couponMutation.data
          )}`,
          hidden: !couponValid,
        },
        {
          lhs: "GST (10% Inclusive)",
          rhs: formatPrice((selectedPlan?.dollar_cost ?? 0) / 11),
        },
      ],
      totalDue: newPlanHeader(),
    };
  };

  const isSuccess = plansQuery.isSuccess;
  const handlePriceSelection = (selected: string) => {
    setSelectedPrice(selected);
    if (plansQuery.data)
      setSelectedPlan(
        getPlanByPriceLookupId(selected, plansQuery.data.availablePlans)
      );
  };

  const getPlanByPriceLookupId = (priceLookupId: string, plans: HubPlan[]) => {
    return plans.find((plan) => plan.price_lookup_id === priceLookupId) ?? null;
  };

  const canContinue = !!(paymentMethodQuery.data?.active && selectedPrice);

  const roundedBudget = roundDownOverageBudget(overageInput, {
    overageRate: selectedPlan?.overage_cost_dollars!,
    planCredits: selectedPlan?.credit_amount ?? 0,
  });

  const [coupon, setCoupon] = useState("");

  const couponMutation = useMutation((promoCode: string) =>
    validateCoupon(promoCode)
  );

  const calculateCouponDiscount = (
    subscription_price: number,
    priceKey: string,
    coupon?: Coupon
  ): number => {
    if (coupon) {
      if (isCouponValid(priceKey, coupon)) {
        if (coupon.amount_off) return coupon.amount_off / 100;
        if (coupon.percent_off)
          return (subscription_price * coupon.percent_off) / 100;
      }
    }
    return 0;
  };

  const generateCouponMessage = (coupon: Coupon) => {
    const discount = coupon.amount_off
      ? `$${coupon.amount_off / 100}`
      : `${coupon.percent_off}%`;
    return `${discount} discount has been applied. Duration: ${coupon.duration}`;
  };

  const isCouponValid = (priceKey: string, coupon?: Coupon): boolean => {
    return (
      !!coupon &&
      !!coupon.applies_to &&
      coupon.applies_to.price_keys.includes(priceKey)
    );
  };

  function newPlanHeader() {
    if (selectedPlan) {
      return formatPrice(
        (creditsQuery.data?.overageDollarsSpent ?? 0) +
          selectedPlan.dollar_cost -
          Math.min(
            calculateCouponDiscount(
              selectedPlan.dollar_cost,
              selectedPlan.price_lookup_id,
              couponMutation.data
            ),
            selectedPlan.dollar_cost
          )
      );
    }
    return "";
  }
  const couponValid = isCouponValid(
    selectedPlan?.price_lookup_id ?? "",
    couponMutation.data
  );

  const handleCancel = () => {
    setSelectedPrice(null);
    setSelectedPlan(null);
    setCoupon("");
    setOverageInput(creditsQuery.data?.overageDollarsLimit ?? overageInput);
    setNewOverageBudget(0);
    couponMutation.reset();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Grid container spacing={2} direction="column">
        <Grid item md={12} xs={12}>
          <Box display="flex" flexDirection="row" gap={2} alignItems="center">
            <Typography variant={"h4"}>Available Plans</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={annualPlans}
                    onChange={(e) => {
                      setAnnualPlans(e.target.checked);
                      handleCancel();
                    }}
                  />
                }
                label="Pay annually"
              ></FormControlLabel>
            </FormGroup>
            <Button
              variant="contained"
              size="small"
              sx={{
                marginLeft: "auto",
              }}
              onClick={() => {
                history.push(Routes.orgSubscription);
              }}
            >
              Back
            </Button>
          </Box>
        </Grid>
        <Grid item>
          {isSuccess && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap", // allows wrapping on smaller screens
                alignItems: "stretch", // ensures equal height cards
              }}
            >
              {plansQuery.data.products
                .filter((product) => {
                  const cadence = annualPlans ? "year" : "month";
                  const price = getPlan(product.name, cadence, plansQuery.data);
                  const isValidPrice = !!price || !product.selfServe;
                  return isValidPrice;
                })
                .map((product) => {
                  const cadence = annualPlans ? "year" : "month";
                  const price = getPlan(product.name, cadence, plansQuery.data);
                  return (
                    <ProductCard
                      priceId={price?.price_lookup_id ?? null} // Price can be null for non-self serve products
                      key={product.name}
                      title={product.name}
                      dollarCost={price?.dollar_cost ?? null}
                      cadence={cadence}
                      selected={selectedPrice === price?.price_lookup_id}
                      disabled={
                        price?.price_lookup_id ===
                          subscriptionQuery.data?.activeSubscription?.plan
                            .price_lookup_id && product.selfServe
                      }
                      isCurrent={
                        price?.price_lookup_id ===
                        subscriptionQuery.data?.activeSubscription?.plan
                          .price_lookup_id
                      }
                      type={product.selfServe ? "selectable" : "contact"}
                      features={product.features}
                      handleSelect={handlePriceSelection}
                    />
                  );
                })}
            </Box>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} direction="row">
        <Grid item xs={12} md={6}>
          <CardHeader title="Overage" subheader="Set a maximum spend limit" />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Overage Limit" />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Overage Limit"
                    type="number"
                    fullWidth
                    sx={{ width: "40%" }}
                    disabled={!selectedPlan}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {selectedPlan && (
                            <InputAdornment position="end">
                              {`/${selectedPlan.cadence}`}
                            </InputAdornment>
                          )}
                        </>
                      ),
                      inputProps: { min: 0, max: 10000 },
                    }}
                    value={overageInput}
                    onChange={(event) => {
                      setOverageInput(
                        Math.min(parseInt(event.target.value), 10000)
                      );
                      const newRoundedBudget = roundDownOverageBudget(
                        Math.min(parseInt(event.target.value), 10000),
                        {
                          overageRate: selectedPlan?.overage_cost_dollars!,
                          planCredits: selectedPlan?.credit_amount ?? 0,
                        }
                      );
                      setNewOverageBudget(newRoundedBudget);
                    }}
                  />
                </Box>
                {selectedPlan &&
                  overageInput !== undefined &&
                  overageInput !== roundedBudget &&
                  overageInput % selectedPlan.overage_cost_dollars! !== 0 && (
                    <Alert variant="outlined" severity="info">
                      The overage limit will automatically be rounded down to{" "}
                      the nearest ${selectedPlan.overage_cost_dollars! * 1000}{" "}
                      increment the equivalent of 1000 credits (
                      {formatPrice(roundedBudget)}) when saved.
                    </Alert>
                  )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <CardHeader
            title="Coupon"
            subheader="Have a coupon code? Enter it here."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Coupon Code"
                    fullWidth
                    disabled={
                      !selectedPlan ||
                      changeType === "downgrade" ||
                      changeType === "same"
                    }
                    onChange={(e) => {
                      couponMutation.reset();
                      setCoupon(e.target.value);
                    }}
                    value={coupon}
                    id="coupon-code"
                    sx={{ width: "40%" }}
                    InputProps={{
                      endAdornment: (
                        <>
                          {couponMutation.isSuccess && couponValid && (
                            <CheckCircleIcon color="success" />
                          )}
                          {couponMutation.isError && (
                            <CancelIcon color="error" />
                          )}
                          {couponMutation.isSuccess && !couponValid && (
                            <CancelIcon color="error" />
                          )}
                        </>
                      ),
                    }}
                  />
                  <Button
                    disabled={
                      !selectedPlan ||
                      !!!coupon ||
                      changeType === "downgrade" ||
                      changeType === "same" ||
                      couponMutation.isLoading
                    }
                    onClick={() => couponMutation.mutate(coupon)}
                    size="small"
                  >
                    Apply
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      couponMutation.reset();
                      setCoupon("");
                    }}
                    disabled={
                      !selectedPlan ||
                      !!!coupon ||
                      changeType === "downgrade" ||
                      changeType === "same" ||
                      couponMutation.isLoading
                    }
                  >
                    Cancel
                  </Button>
                </Box>
                {couponMutation.isSuccess &&
                  isCouponValid(
                    selectedPlan?.price_lookup_id ?? "",
                    couponMutation.data
                  ) && (
                    <Alert variant="outlined" severity="success">
                      {generateCouponMessage(couponMutation.data)}
                    </Alert>
                  )}
                {couponMutation.isError && (
                  <Alert variant="outlined" severity="error">
                    {(couponMutation.error as ReactQueryMutationError).message}
                  </Alert>
                )}
                {couponMutation.isSuccess && !couponValid && (
                  <Alert variant="outlined" severity="error">
                    Coupon code cannot be applied to the selected subscription
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <CardHeader title="Confirm Changes" />
        </Grid>
        <Grid item xs={12} md={6}>
          <ConfirmChangesSection
            changes={getChangeSummary()}
            isLoading={subscriptionMutation.isLoading}
            canSubmit={canContinue}
            onCancel={handleCancel}
            canCancel={!!selectedPlan && !subscriptionMutation.isLoading}
            onSubmit={() => {
              if (!selectedPrice) {
                throw Error(
                  "Submitted subcription form without selecting a price!"
                );
              }
              subscriptionMutation.mutate({
                priceLookupId: selectedPrice,
                overageLimitDollars: newOverageBudget,
                coupon: couponValid ? couponMutation.data?.id : undefined,
              });
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
